import { Observable } from 'rxjs/internal/Observable';

import { AxisPositionType } from 'app/models/api-dto.model';

export interface ReportTemplateDto {
    id: string;
    sections: SectionTemplateDto[];
    creationDate?: string;
}

export interface SectionTemplateDto {
    id: string;
    sortId: number;
    title: string;
    headers: HeaderTemplateElementDto[];
    charts: ChartTemplateElementDto[];
    scatterPlotCharts: ScatterPlotElementDto[];
    tables: TableTemplateElementAggregatedDto[];
    consumptionTables: ConsumptionTableTemplateElementAggregatedDto[];
    kpiSets: KpiSetsElementDto[];
    sorting: ElementSort[];
}

export interface ElementSort {
    elementId: string;
    sortId: number;
}

export interface HeaderTemplateElementDto {
    id: string;
    title: string;
}

export interface ChartTemplateElementDto {
    id: string;
    title: string;
    description: string;
    datapoints: HistoricalDatapointDto[];
    legend: LegendDto[];
    kpi: ChartKpiDto;
    datapointFilters: DatapointFiltersDto[];
    inOperationFilterType: InOperationFilterTypeDto;
}

export interface ScatterPlotElementDto {
    id: string;
    title: string;
    description: string;
    datapoints: HistoricalDatapointDto[];
    kpi: ScatterplotKpiDto;
    legend: LegendDto[];
    datapointFilters: DatapointFiltersDto[];
    inOperationFilterType: InOperationFilterTypeDto;
}

export interface KpiSetsElementDto {
    id: string;
    name: string;
    linkedVisuals: LinkedVisuals[];
}

export interface LinkedVisuals {
    visualId: string;
    weight: number;
    title?: string;
    lowerLimit?: number;
}

export interface LegendDto {
    datapointId: string;
    color: string;
    label: string;
}
export interface LegendItem {
    id: string;
    name: string;
    color: string | Highcharts.GradientColorObject | Highcharts.PatternObject;
    unit: string;
    yAxisId: string;
    visible: boolean;
    disabled?: boolean;
    value$?: Observable<string | number>;
}

export enum InOperationFilterTypeDto {
    NoTimeFilter = 0,
    InOperation = 1,
    NotInOperation = 2,
}

export interface OperationTimeDto {
    siteId: string;
    weekdays: WeekdayNumber[];
    startTime: string;
    endTime: string;
}

export type WeekdayNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface KpiDto {
    id: string;
    target: number;
}

export interface ChartKpiDto extends KpiDto {
    bandwidth: KpiBandwidthDto;
}

export interface ScatterplotKpiDto extends KpiDto {
    coordinates: KpiCoordinatesDto[];
    limits: KpiLimitsDto;
}

export interface KpiCoordinatesDto {
    datapointId: HistoricalDatapointDto;
    values: number[];
}

export interface KpiLimitsDto {
    above: number;
    below: number;
}

export interface KpiBandwidthDto {
    minimumPoint: number;
    maximumPoint: number;
}

export interface HistoricalDatapointDto {
    siteId: string;
    deviceGroupId: string;
    deviceId: string;
    variableId: string;
    unit: string;
    positionType?: AxisPositionType;
}

export interface DatapointFiltersDto extends HistoricalDatapointDto {
    filterType: FilterTypes;
    customValue: number;
}

export interface HistoricalAggregatedDatapointDto extends HistoricalDatapointDto {
    label: string;
    aggregateChoices: string[];
}

export type HistoricalDatapoint =
    | HistoricalDatapointDto
    | HistoricalAggregatedDatapointDto
    | DatapointFiltersDto;

export interface HistoricalDatapointPropertiesDto {
    pointReference?: string;
}

export interface TableTemplateElementAggregatedDto {
    id: string;
    title: string;
    description: string;
    granularity: string;
    datapoints: HistoricalAggregatedDatapointDto[];
}

export interface ConsumptionTableTemplateElementAggregatedDto {
    id: string;
    title: string;
    description: string;
    granularity: string;
    datapoints: HistoricalAggregatedDatapointDto[];
}

export enum FilterTypes {
    off = 0,
    on = 1,
    greater_than = 2,
    less_than = 3,
}

export enum FilterTypeSymbols {
    '=',
    '= ',
    '>',
    '<',
}

export enum FilterTypesNames {
    'REPORT_EDITOR.EDIT_ELEMENT.FILTER_OFF',
    'REPORT_EDITOR.EDIT_ELEMENT.FILTER_ON',
    'REPORT_EDITOR.EDIT_ELEMENT.FILTER_GREATER_THAN',
    'REPORT_EDITOR.EDIT_ELEMENT.FILTER_LESS_THAN',
}
